import * as React from 'react';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import classnames from 'classnames';
import Compressor from 'compressorjs';
import Cookies from 'js-cookie';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import qs from 'query-string';

import Accordion from '@components/Accordion';
import { AttachmentImage } from '@components/Attachment';
import { ACCEPTED_FILE_TYPES } from '@components/Attachment/AttachmentsModal';
import Button from '@components/Button';
import Checkbox from '@components/Checkbox';
import MultiSectionDropdown from '@components/DropdownMultiSection/MultiSectionDropdown';
import Input from '@components/Input';

import { PRIVATE_APP_TOKEN } from '@private-crm-app/constants';

import { addError } from '@actions/appState';
import {
  getCRMAnonymousIssueDetails,
  getCRMMapConfig,
  getCRMSuggestedCategories,
  saveCRMAnonymousIssueDetails,
  submitIssue,
  VALIDATE_CRM_FAILURE,
  validateCRMToken,
} from '@actions/crm';
import { getSSOConfig, getSSOToken, getSSOUserInfo } from '@actions/crmSso';
import { getCRMSignedURL, S3_CRM_SIGNED_URL_SUCCESS } from '@actions/s3';

import {
  getEmptyErrorState,
  getFileType,
  getIndexedFileName,
  getS3URL,
  humanFileSize,
  isDesktop,
  MAX_ATTACHMENT_FILE_SIZE,
  randomCodeGenerator,
  scrollTo,
} from '@utils/AppUtil';
import CustomFieldUtil from '@utils/CustomFieldUtil';
import CyberdyneValidator from '@utils/cyberdyneValidator';
import { getFormattedDeviceDetails } from '@utils/DeviceDetailsUtil';
import ErrorMessageGenerator, { FieldValidationType } from '@utils/ErrorMessageGenerator';
import LocationUtil from '@utils/LocationUtil';
import { getLabels } from '@utils/MixPanelLabels';
import { trackOnElevioPortal } from '@utils/MixPanelUtils';
import { s3uploadFile } from '@utils/S3Util';
import { getTokenFromLocalStorage, setTokenInLocalStorage } from '@utils/SSOUtil';

import CRMLocationField from '../CRMIssueFormFields/CRMLocationField';

import './style.scss';

export interface ICRMIssueFormProps extends RouteComponentProps<{ crmToken: string }> {
  // From redux
  agencyDetails: IAgencyDetails;
  issue: ICRMIssueSubmissionResponse;
  categories: ICRMCategoryResponse[];
  validateCRMToken: TValidateCRMToken;
  saveCRMAnonymousIssueDetails: TSaveCRMAnonymousIssueDetails;
  getCRMAnonymousIssueDetails: TGetCRMAnonymousIssueDetails;
  anonymousSubmission?: ICRMIssueAnonymousDetails;
  submitIssue: TSubmitIssue;
  s3Operation?: S3Operation;
  acceptedFileTypes?: string[];
  maxFileSize?: number;
  isSMSEnabled: boolean;
  postAnonymous: boolean;
  getCRMSignedURL: TGetCRMSignedURL;
  addError: TAddError;
  getCRMMapConfig: TGetCRMMapConfig;
  getSuggestedCategories: TGetCRMSuggestedCategory;
  contactPreferenceNote: string;
  getSsoToken: TGetIDPToken;
  getSsoConfig: TGetSsoConfig;
  getSsoUserInfo: TGetUserInfo;
  gisVersion?: GISVersion;
}

export interface ICRMIssueFormState {
  markerCenter: IMapCoordinate;
  activeStep: number;
  categoryFilled: boolean;
  description: string;
  firstName: string;
  lastName: string;
  email: string;
  isAnonymous: boolean;
  hasSubscribed: boolean;
  phoneNumber: string;
  workPhone: string;
  suggestedCategories: number[];
  error: {
    notificationNote: IFieldValidation;
    description: IFieldValidation;
    firstName: IFieldValidation;
    lastName: IFieldValidation;
    email: IFieldValidation;
    location: IFieldValidation;
    phoneNumber: IFieldValidation;
    workPhone: IFieldValidation;
  };
  files: Map<string, IFileUpload>;
  location: ILocation;
  category: ICRMCategoryResponse;
  fieldValues: IFieldState[];
  categoryfieldValues: IFieldValues;
  isEmailNotificationPreferenceSelected: boolean;
  isSMSNotificationPreferenceSelected: boolean;
  notificationNote: string;
  isLoginButtonVisible: boolean;
  accessToken: string;
  ssoBaseUrl: string;
  ssoClientId: string;
  ssoCityName: string;
}

const errMsgGen = new ErrorMessageGenerator();
const cyberdyneValidator = new CyberdyneValidator();
const ATTACHMENT_UPLOAD_FAILED_MESSAGE =
  'The attachment did not attach. You can retry the upload or submit without it.';
const locationUtil = new LocationUtil();
const customFieldUtil = new CustomFieldUtil();

export class CRMIssueForm extends React.Component<ICRMIssueFormProps, ICRMIssueFormState> {
  static defaultProps: Partial<ICRMIssueFormProps> = {
    acceptedFileTypes: ACCEPTED_FILE_TYPES,
    maxFileSize: MAX_ATTACHMENT_FILE_SIZE,
    s3Operation: S3Operation.CRM_ISSUE_ATTACHMENT,
  };

  constructor(props: ICRMIssueFormProps) {
    super(props);

    this.state = this.getEmptyState();
  }

  componentDidMount() {
    const { crmToken } = this.props.match.params;
    this.props.validateCRMToken(crmToken).then((action) => {
      if (action.type === VALIDATE_CRM_FAILURE || !action.response.isActive) {
        this.props.history.replace('/crm/error');
      }

      this.setSsoSettings();

      const { ELEVIO_APP_LAUNCH } = getLabels();

      trackOnElevioPortal(ELEVIO_APP_LAUNCH, {
        'Device Details': getFormattedDeviceDetails(),
      });
    });

    if (this.props.location.search.includes('translate')) {
      this.setupTranslate();
    }

    this.props.getCRMMapConfig(crmToken).then((_) => {
      if (this.props.location.search.includes('state')) {
        const { crmToken } = this.props.match.params;
        const queryParams = qs.parse(this.props.location.search);
        const anonymousId = queryParams.state;
        this.props.getCRMAnonymousIssueDetails(crmToken, anonymousId);
      }

      if (this.props.location.search.includes('isIFrame')) {
        const { crmToken } = this.props.match.params;
        const queryParams = qs.parse(this.props.location.search);
        const anonymousId = queryParams.anonymousId;
        this.props.getCRMAnonymousIssueDetails(crmToken, anonymousId);
      }
    });
  }

  componentDidUpdate(previousProps: ICRMIssueFormProps, previousState: ICRMIssueFormState) {
    if (
      this.props.anonymousSubmission &&
      (previousProps.anonymousSubmission !== this.props.anonymousSubmission ||
        previousProps.categories !== this.props.categories)
    ) {
      this.setAnonymousIssueDetails(this.props.anonymousSubmission);
    }

    const queryParams = qs.parse(location.search);
    let isCategoryMatched = false;
    let categoryParam: string;

    if (queryParams.issueDescription && this.props.categories && this.props.categories !== previousProps.categories) {
      categoryParam = queryParams.category;
      if (categoryParam) {
        this.setState({ description: queryParams.issueDescription }, () => {
          this.validateDescriptionForm();
          this.props.categories.map((category) => {
            if (category.name.toLowerCase() === categoryParam.toLowerCase() && category.isActive) {
              this.handleCategorySelection(category);
              isCategoryMatched = true;
            }
          });
          if (!isCategoryMatched) {
            this.onDescriptionSubmit();
          }
          this.moveToStep(this.state.activeStep + 1);
        });
      }
    }
  }

  setSsoSettings = () => {
    const { crmToken } = this.props.match.params;

    if (this.props.location.search.includes('isIFrame')) {
      this.setState({ isLoginButtonVisible: false });
    } else {
      this.props.getSsoConfig(crmToken).then((response) => {
        if (response && response.response) {
          this.setState((prevState) => ({
            ...prevState,
            ssoBaseUrl: response.response.ssoBaseUrl,
            ssoClientId: response.response.clientAppId,
            ssoCityName: response.response.agencyCity,
          }));
        }
      });
    }

    if (this.props.location.search.includes('token')) {
      const queryParams = qs.parse(this.props.location.search);

      setTokenInLocalStorage(queryParams.token);
    }

    this.getSsoToken();
  };

  setupTranslate = () => {
    const params = qs.parse(this.props.location.search);
    Cookies.set('googtrans', params.translate, {
      sameSite: 'None',
      secure: true,
    });
    const script = document.createElement('script');
    script.src =
      // tslint:disable-next-line:max-line-length
      '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
    script.async = true;
    document.body.appendChild(script);
    const script2 = document.createElement('script');
    // tslint:disable: max-line-length
    script2.innerHTML = `
    function googleTranslateElementInit() {
      new google.translate.TranslateElement({pageLanguage: 'en'},'google_translate_element');
    }
    `;
    document.body.appendChild(script2);
  };

  getEmptyState = (): ICRMIssueFormState => ({
    markerCenter: null,
    description: '',
    categoryFilled: true,
    firstName: '',
    lastName: '',
    email: '',
    suggestedCategories: [],
    activeStep: 0,
    isAnonymous: false,
    phoneNumber: '',
    workPhone: '',
    hasSubscribed: true,
    error: {
      notificationNote: getEmptyErrorState(),
      description: getEmptyErrorState(),
      firstName: getEmptyErrorState(),
      lastName: getEmptyErrorState(),
      email: getEmptyErrorState(),
      location: getEmptyErrorState(),
      phoneNumber: getEmptyErrorState(),
      workPhone: getEmptyErrorState(),
    },
    files: new Map(),
    location: null,
    category: null,
    fieldValues: [],
    categoryfieldValues: {},
    isEmailNotificationPreferenceSelected: false,
    isSMSNotificationPreferenceSelected: false,
    notificationNote: '',
    isLoginButtonVisible: true,
    accessToken: null,
    ssoBaseUrl: null,
    ssoClientId: null,
    ssoCityName: null,
  });

  renderNavigationBar = () => {
    const { ssoBaseUrl, ssoClientId, ssoCityName, isLoginButtonVisible } = this.state;
    const currentLocationPath = window.location.href.split('?')[0];

    let ssoAuthUrl: string;

    if (ssoBaseUrl && ssoClientId) {
      ssoAuthUrl = `${ssoBaseUrl}/authorize?client_id=${ssoClientId}&redirect_uri=${currentLocationPath}&scope=openid+profile+email+phone+address&response_type=code`;
    }

    return (
      <nav className={classnames('crm-submission__navigation-bar')}>
        <h1>Submit a request</h1>
        {isLoginButtonVisible && ssoAuthUrl && (
          <Button
            onClick={async () => {
              await this.saveAnonymousDetailsAndNavigate(ssoAuthUrl);
            }}
          >
            Click here to login with your {ssoCityName} account
          </Button>
        )}
      </nav>
    );
  };

  saveAnonymousDetailsAndNavigate = async (authUrl) => {
    const issueDetails = this.getIssueSubmissionRequest(true, true) as unknown as ICRMIssueAnonymousDetails;
    if (isEmpty(issueDetails.location)) {
      omit(issueDetails, 'location');
    }

    const queryParams = qs.parse(this.props.location.search);
    const anonymousId = queryParams.anonymousId || randomCodeGenerator(30);
    const { crmToken } = this.props.match.params;
    if (issueDetails.description) {
      await this.props.saveCRMAnonymousIssueDetails(crmToken, anonymousId, issueDetails);
      window.location.href = `${authUrl}&state=${anonymousId}`;
    } else {
      window.location.href = authUrl;
    }
  };

  saveAnonymousDetailsIFrame = async () => {
    if (this.props.location.search.includes('isIFrame')) {
      let issueDetails = this.getIssueSubmissionRequest(true, true) as unknown as ICRMIssueAnonymousDetails;
      if (isEmpty(issueDetails.location)) {
        issueDetails = omit(issueDetails, 'location');
      }

      const queryParams = qs.parse(this.props.location.search);
      const anonymousId = queryParams.anonymousId || randomCodeGenerator(30);
      const { crmToken } = this.props.match.params;
      if (issueDetails.description) {
        await this.props.saveCRMAnonymousIssueDetails(crmToken, anonymousId, issueDetails);
      }
    }
  };

  getSsoToken = () => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const beverlyHillAuthCode = urlSearchParams.get('code');

    const existingToken = getTokenFromLocalStorage();
    const { crmToken } = this.props.match.params;

    if (existingToken) {
      this.setState((prevState) => ({
        ...prevState,
        accessToken: existingToken,
        isLoginButtonVisible: false,
      }));

      this.fetchSsoUserInfo(crmToken, existingToken);
    } else if (beverlyHillAuthCode) {
      this.setState({ isLoginButtonVisible: false });
      urlSearchParams.delete('code');
      this.props.history.replace({ search: urlSearchParams.toString() });
      this.props.getSsoToken(crmToken, beverlyHillAuthCode).then((result) => {
        setTokenInLocalStorage(result.response.token);

        this.setState((prevState) => ({
          ...prevState,
          accessToken: result.response.token,
        }));

        this.fetchSsoUserInfo(crmToken, result.response.token);
      });
    }
  };

  fetchSsoUserInfo = (crmToken: string, idpAccessToken) => {
    const idpUserInfoRequest: ISsoUserInfoRequest = {
      token: idpAccessToken,
    };

    this.props.getSsoUserInfo(crmToken, idpUserInfoRequest).then((response) => {
      if (response && response.response) {
        this.setState((prevState) => ({
          ...prevState,
          firstName: response.response.firstName,
          lastName: response.response.lastName,
          email: response.response.email,
          phoneNumber: response.response.phoneNumber,
          workPhone: response.response.workPhone,
        }));
      }
    });
  };

  handleChange = (e?: React.SyntheticEvent<any>) => {
    const { name, value } = e.currentTarget;
    this.setState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  handleBlur = (e: React.SyntheticEvent<any>) => {
    const { name, value } = e.currentTarget;

    switch (name) {
      case 'phoneNumber':
        if (value && this.validatePhone(value).isValid && !this.state.isSMSNotificationPreferenceSelected) {
          this.setState({ isSMSNotificationPreferenceSelected: true });
        }
        break;

      case 'email':
        if (value && this.validateEmail(value).isValid && !this.state.isEmailNotificationPreferenceSelected) {
          this.setState({ isEmailNotificationPreferenceSelected: true });
        }
        break;

      default:
        break;
    }
  };

  handleAnonymousChange = (e: React.MouseEvent<HTMLButtonElement>) => {
    const value = e.currentTarget && e.currentTarget.value;
    this.setState((prevState) => ({
      ...prevState,
      isAnonymous: value as unknown as boolean,
    }));
  };

  handleSubscriptionChange = (e: React.SyntheticEvent<any>) => {
    const { hasSubscribed } = this.state;

    this.setState((prevState) => ({
      ...prevState,
      hasSubscribed: !hasSubscribed,
    }));
  };

  removeFile = (fileKey: string) => {
    const currentFiles = this.state.files;
    const newFiles = new Map(currentFiles);
    newFiles.delete(fileKey);

    this.setState({
      files: newFiles,
    });
  };

  getEmptyFieldValues = (categoryId: number | string): IFieldValues => {
    const { categories } = this.props;
    const category = categories ? categories.find((item) => item.id == categoryId) : null;
    const fieldValues =
      category && category.fields
        ? category.fields.reduce((acc, curr: any) => {
            const id = curr.hasOwnProperty('_id') ? curr._id : curr.id;
            if (curr.isActive && !curr.isDeleted) {
              acc[id] = {
                id,
                isRequired: curr.isRequired,
                label: curr.label,
                type: curr.type,
                value: '',
                error: getEmptyErrorState(),
              };
            }

            return acc;
          }, {})
        : {};

    return fieldValues;
  };

  handleCategorySelection = (category: ICRMCategoryResponse) => (e?: React.SyntheticEvent<any>) => {
    const fieldValues = customFieldUtil.getDefaultFieldValuesState(category.fields, null, true);
    let categoryfieldValues = this.state.categoryfieldValues;
    categoryfieldValues = this.getEmptyFieldValues(+category?.id);
    this.setState({ categoryFilled: true });
    this.setState({ category, fieldValues, error: this.getEmptyState().error });
    this.setState((prevState) => ({
      ...prevState,
      categoryfieldValues,
    }));
  };

  getEmptyAttributes = () => {
    const res = [];
    const lookup = {
      city: 'City',
      state: 'State',
      zip: 'Zip',
    };

    for (const attr in lookup) {
      if (!this.state.location[attr]) {
        res.push(lookup[attr]);
      }
    }

    return res.join(', ');
  };

  validateDescriptionForm = () => {
    let isValid = true;
    const { description, category: selectedCategory } = this.state;
    const error = this.getEmptyState().error;
    if (cyberdyneValidator.isEmptyString(description)) {
      error.description = {
        errorState: IValidationState.ERROR,
        errorMessage: errMsgGen.getMandatoryFieldMessage('Description'),
      };

      isValid = false;
    } else if (cyberdyneValidator.isStringGreaterThanLength(description.trim(), 2000)) {
      error.description = {
        errorState: IValidationState.ERROR,
        errorMessage: errMsgGen.getCharacterLimitMessage('Issue description', 2000),
      };

      isValid = false;
    }

    if (this.isAddDisabled()) {
      isValid = false;
    }

    if (selectedCategory === null) {
      this.setState({ categoryFilled: false });
    }

    if (!this.validateCategoryCustomFieldForm()) {
      isValid = false;
    }
    this.setState({ error });

    return isValid;
  };

  validatePhone = (phoneNumber: string): { isValid: boolean; error?: IFieldValidation } => {
    if (phoneNumber && phoneNumber.length) {
      if (!cyberdyneValidator.isPhone(phoneNumber.replace(/-/g, ''))) {
        return {
          isValid: false,
          error: {
            errorState: IValidationState.ERROR,
            errorMessage: errMsgGen.getInvalidFieldMessage('Cell Phone', FieldValidationType.PHONE),
          },
        };
      }
    }

    return { isValid: true };
  };

  validateEmail = (email: string): { isValid: boolean; error?: IFieldValidation } => {
    if (email && email.length) {
      if (cyberdyneValidator.isStringGreaterThanLength(email, EMAIL_CONFIG.LENGTH)) {
        return {
          isValid: false,
          error: {
            errorState: IValidationState.ERROR,
            errorMessage: errMsgGen.getCharacterLimitMessage('Email', EMAIL_CONFIG.LENGTH),
          },
        };
      } else if (email && !cyberdyneValidator.isEmail(email)) {
        return {
          isValid: false,
          error: {
            errorState: IValidationState.ERROR,
            errorMessage: errMsgGen.getInvalidFieldMessage('Email', FieldValidationType.EMAIL),
          },
        };
      }
    }

    return { isValid: true };
  };

  validateContactForm = () => {
    let isValid = true;
    const {
      firstName,
      lastName,
      email,
      hasSubscribed,
      phoneNumber,
      workPhone,
      isAnonymous,
      isEmailNotificationPreferenceSelected,
      isSMSNotificationPreferenceSelected,
      notificationNote,
    } = this.state;
    const error = this.getEmptyState().error;

    const setValidationAndError = (field: string, validation: { isValid: boolean; error?: IFieldValidation }) => {
      if (!validation.isValid) {
        isValid = false;
      }
      if (validation.error) {
        error[field] = validation.error;
      }
    };

    if (!this.props.postAnonymous || (this.props.postAnonymous && !isAnonymous)) {
      if (!firstName || (firstName && !firstName.trim())) {
        error.firstName = {
          errorState: IValidationState.ERROR,
          errorMessage: errMsgGen.getMandatoryFieldMessage('First Name'),
        };

        isValid = false;
      } else if (cyberdyneValidator.isStringGreaterThanLength(firstName.trim(), 20)) {
        error.firstName = {
          errorState: IValidationState.ERROR,
          errorMessage: errMsgGen.getCharacterLimitMessage('First Name', 20),
        };

        isValid = false;
      }

      if (!lastName || (lastName && !lastName.trim())) {
        error.lastName = {
          errorState: IValidationState.ERROR,
          errorMessage: errMsgGen.getMandatoryFieldMessage('Last Name'),
        };

        isValid = false;
      } else if (cyberdyneValidator.isStringGreaterThanLength(lastName.trim(), 20)) {
        error.lastName = {
          errorState: IValidationState.ERROR,
          errorMessage: errMsgGen.getCharacterLimitMessage('Last Name', 20),
        };

        isValid = false;
      }
    }

    if (!isAnonymous && hasSubscribed) {
      if (workPhone && workPhone.length) {
        if (!cyberdyneValidator.isPhone(workPhone.replace(/-/g, ''))) {
          error.workPhone.errorState = IValidationState.ERROR;
          error.workPhone.errorMessage = errMsgGen.getInvalidFieldMessage('Work Phone', FieldValidationType.PHONE);
          isValid = false;
        }
      } else {
        const phoneValidation = this.validatePhone(phoneNumber);
        setValidationAndError('phoneNumber', phoneValidation);

        const emailValidation = this.validateEmail(email);
        setValidationAndError('email', emailValidation);
      }

      if (isEmailNotificationPreferenceSelected) {
        if (!email) {
          error.email = { errorState: IValidationState.ERROR, errorMessage: 'Enter the details.' };

          isValid = false;
        }
      }

      if (this.props.isSMSEnabled && isSMSNotificationPreferenceSelected) {
        if (!phoneNumber) {
          error.phoneNumber = { errorState: IValidationState.ERROR, errorMessage: 'Enter the details.' };
          isValid = false;
        }
      }

      if (notificationNote && cyberdyneValidator.isStringGreaterThanLength(notificationNote, 500)) {
        error.notificationNote.errorState = IValidationState.ERROR;
        error.notificationNote.errorMessage = errMsgGen.getCharacterLimitMessage('Notification Note', 500);
        isValid = false;
      }
    }

    this.setState({ error });

    return isValid;
  };

  validateLocationForm = () => {
    let isValid = true;
    const {
      location,
      category,
      error: { location: locationError },
    } = this.state;
    const error = this.getEmptyState().error;
    if (category && category.isLocationIncluded) {
      if (!location && category.isLocationRequired) {
        error.location.errorState = IValidationState.ERROR;
        error.location.errorMessage = errMsgGen.getMandatoryFieldMessage('location');
        isValid = false;
      }
    }

    if (location) {
      const missingAttr = this.getEmptyAttributes();
      if (missingAttr) {
        error.location.errorState = IValidationState.ERROR;
        error.location.errorMessage = `Missing mandatory location field(s) - ${missingAttr}`; // tslint:disable-line
        isValid = false;
      }

      if (!!locationError.errorMessage) {
        isValid = false;
        error.location = locationError;
      }
    }

    this.setState({ error });

    return isValid;
  };

  getIssueSubmissionRequest = (skipUserDetails = false, includeCurrentPageNo = false) => {
    if (this.isAddDisabled()) {
      return null;
    }
    let location = null;

    const {
      files,
      categoryfieldValues,
      firstName,
      lastName,
      hasSubscribed,
      isAnonymous,
      email,
      phoneNumber,
      workPhone,
      notificationNote,
      isEmailNotificationPreferenceSelected,
      isSMSNotificationPreferenceSelected,
    } = this.state;

    const shouldSkipUserInfo = isAnonymous || skipUserDetails;

    const notificationPreference: NotificationPreferenceType[] = [];
    if (hasSubscribed) {
      if (isEmailNotificationPreferenceSelected) {
        notificationPreference.push(NotificationPreferenceType.EMAIL);
      }
      if (isSMSNotificationPreferenceSelected) {
        notificationPreference.push(NotificationPreferenceType.SMS);
      }
    }
    const crmAttachmentFiles: ICRMIssueAttachment[] = Array.from(files.keys())
      .map((fileKey) => {
        const file = files.get(fileKey);

        return {
          fileName: fileKey,
          fileSize: humanFileSize(file.details.size),
          fileURL: file.s3SavedUrl,
        };
      })
      .filter((attachment) => !!attachment.fileURL);

    if (this.state.location) {
      const { lat, long, streetAddress, placeName, ...rest } = this.state.location;

      location = {
        latitude: lat,
        longitude: long,
        locationSource: LocationSource.CITIZEN_SUBMISSION,
        streetAddress: streetAddress || null,
        placeName: placeName || null,
        ...rest,
      };
    }

    let currentPageNo;
    if (includeCurrentPageNo) {
      if (this.props.location.search.includes('isIFrame')) {
        currentPageNo = this.state.activeStep >= this.getLastStep() ? 2 : 1;
      } else {
        currentPageNo = this.state.activeStep >= this.getLastStep() + 1 ? 2 : 1;
      }
    }

    let auth0UserId;
    try {
      const tokenValue = localStorage.getItem(PRIVATE_APP_TOKEN);
      if (tokenValue) {
        const decodedPayload = JSON.parse(atob(tokenValue.split('.')[1]));

        // Check if the email from the token matches the email of the current user
        const tokenEmail = decodedPayload.email;
        const currentUserEmail = email?.trim()?.toLowerCase();

        if (currentUserEmail && tokenEmail && tokenEmail.toLowerCase() !== currentUserEmail) {
          console.log('Token email does not match current user email, clearing token', {
            tokenEmail: tokenEmail,
            currentEmail: currentUserEmail,
          });
          auth0UserId = null;
        } else {
          // Get Auth0 user ID from token
          auth0UserId = decodedPayload.auth0UserId;
        }
      } else {
        console.log('No private app token found, using anonymous submission');
        auth0UserId = null;
      }
    } catch (error) {
      console.error('Error decoding token:', error);
      auth0UserId = null;
    }

    return {
      description: this.state.description,
      isAnonymous: this.state.isAnonymous,
      attachments: crmAttachmentFiles,
      category: {
        id: this.state.category.id,
        fields: Object.keys(categoryfieldValues).map((fieldValue) => categoryfieldValues[fieldValue]),
      },
      userId: auth0UserId,
      ...(location ? { location } : {}),
      ...(!shouldSkipUserInfo
        ? {
            customerInfo: {
              firstName: firstName || null,
              lastName: lastName || null,
              hasSubscribed,
              email: email || null,
              phoneNumber: phoneNumber ? phoneNumber.replace(/-/g, '') : null,
              workPhone: workPhone ? workPhone.replace(/-/g, '') : null,
              notificationPreference,
              notificationNote: notificationNote ? notificationNote : null,
              ...(typeof window !== 'undefined' && window.location.pathname.startsWith('/crm/') && email
                ? { existingContactEmail: email }
                : {}),
            },
          }
        : {}),
      ...(includeCurrentPageNo ? { currentPageNo } : {}),
    };
  };

  setAnonymousIssueDetails = (crmAnonymousIssueDetails: ICRMIssueAnonymousDetails) => {
    const { latitude = null, longitude = null, ...rest } = crmAnonymousIssueDetails.location || {};
    const attachments = crmAnonymousIssueDetails.attachments || [];
    const files = new Map();
    attachments.forEach((attachment) => {
      files.set(attachment.fileName, {
        details: new File([''], attachment.fileName, {}),
        s3SavedUrl: attachment.fileURL,
        s3SignedURL: attachment.signedURL,
        progress: 100,
        error: {
          file: {
            errorState: null,
            errorMessage: null,
          },
        },
      });
    });

    this.setState({
      description: crmAnonymousIssueDetails.description,
      isAnonymous: crmAnonymousIssueDetails.isAnonymous,
      ...(crmAnonymousIssueDetails.location && {
        location: {
          lat: latitude,
          long: longitude,
          ...rest,
        },
      }),
      files,
      activeStep: crmAnonymousIssueDetails.currentPageNo === 1 ? 0 : this.getLastStep() + 2,
    });

    if (this.props.categories) {
      let isCategoryMatched;
      this.props.categories.map((category) => {
        if (category.id === crmAnonymousIssueDetails.category.id && category.isActive) {
          const fieldValues = customFieldUtil.getDefaultFieldValuesState(category.fields, null, true);
          customFieldUtil.setRequestKeyValues(fieldValues, crmAnonymousIssueDetails.category.fields);
          this.setState({ category, fieldValues });
          isCategoryMatched = true;
        }
      });
      if (!isCategoryMatched) {
        this.onDescriptionSubmit();
      }
    }
  };

  handleSubmit = (e: React.SyntheticEvent<any>) =>
    new Promise<void>((resolve) => {
      e.preventDefault();
      if (this.validateContactForm()) {
        const { crmToken } = this.props.match.params;
        this.props.submitIssue(crmToken, this.getIssueSubmissionRequest()).then((response) => resolve());
      } else {
        return resolve();
      }
    });

  onDescriptionSubmit = () => {
    this.saveAnonymousDetailsIFrame();
  };

  onDragEnter = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
  };

  onDragExit = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
  };

  onDragLeave = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
  };

  onDragOver = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
  };

  onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.handleAttachmentChange(e);
  };

  getFileEmptyErrorState = () => ({
    file: getEmptyErrorState(),
  });

  getValidFiles = (files: FileList) => {
    let validFiles = [];
    let hasInvalidFiles = false;

    for (const index in files) {
      if (!isNaN(+index)) {
        const splitText = files[index].name.split('.');
        const fileType = `.${splitText[splitText.length - 1]}`;

        if (this.props.acceptedFileTypes.includes(fileType.toLowerCase())) {
          validFiles = validFiles.concat(files[index]);
        } else {
          hasInvalidFiles = true;
        }
      }
    }

    if (hasInvalidFiles && this.props.addError) {
      this.props.addError('One/more files with unsupported format is/are discarded.');
    }

    return validFiles;
  };

  isAddDisabled = () => {
    const files = this.state.files;

    for (const file of Array.from(files.values())) {
      if (file?.error?.file?.errorState === IValidationState.ERROR) {
        continue;
      }

      if (file.progress !== 100 || !file.s3SavedUrl) {
        return true;
      }
    }

    return false;
  };

  handleLocationChange = (location: ILocation) => {
    this.setState({ location });
  };

  handleMarkerChange = (mapCenter: IMapCoordinate) => {
    this.setState({ markerCenter: mapCenter });
  };

  handleAttachmentChange = (e) => {
    let files = null;
    if (e.dataTransfer) {
      files = this.getValidFiles(e.dataTransfer.files);
    } else if (e.target) {
      files = this.getValidFiles(e.target.files);
    }

    const isImage = (file) => {
      const split = file.name.split('.');
      const extension = split[split.length - 1];

      return /png|jpe?g/.test(extension.toLowerCase());
    };

    const onUploadProgress = (progressEvent, key) => {
      const progress: number = (progressEvent.loaded / progressEvent.total) * 100;

      const newFiles = new Map(this.state.files);

      if (newFiles.get(key)) {
        const currentFile = newFiles.get(key);

        newFiles.set(key, {
          ...currentFile,
          progress,
        });

        this.setState({
          files: newFiles,
        });
      }
    };

    const onUploadComplete = (response, key) => {
      const newFiles = new Map(this.state.files);
      const s3SavedUrl = getS3URL(response);

      if (newFiles.get(key) && s3SavedUrl) {
        const currentFile = newFiles.get(key);

        newFiles.set(key, {
          ...currentFile,
          progress: 100,
          s3SavedUrl,
        });

        this.setState({
          files: newFiles,
        });
      } else {
        onUploadFailed(new Error('S3 upload response did not include a saved file location.'), key);
      }
    };

    const onUploadFailed = (error, key) => {
      const newFiles = new Map(this.state.files);

      if (newFiles.get(key)) {
        const currentFile = newFiles.get(key);

        newFiles.set(key, {
          ...currentFile,
          progress: 100,
          s3SavedUrl: null,
          error: {
            ...currentFile.error,
            file: {
              errorState: IValidationState.ERROR,
              errorMessage: ATTACHMENT_UPLOAD_FAILED_MESSAGE,
            },
          },
        });

        this.setState({
          files: newFiles,
        });

        if (this.props.addError) {
          this.props.addError(ATTACHMENT_UPLOAD_FAILED_MESSAGE);
        }
      }
    };

    files.map((file) => {
      const reader = new FileReader();
      const fileKey = getIndexedFileName(file.name);

      reader.onloadend = () => {
        const newFiles = new Map(this.state.files);
        newFiles.set(fileKey, {
          data: reader.result as string,
          details: file,
          progress: 0,
          s3SavedUrl: '',
          error: this.getFileEmptyErrorState(),
        });

        this.props.getCRMSignedURL(this.props.match.params.crmToken, this.props.s3Operation, fileKey).then((action) => {
          if (action.type === S3_CRM_SIGNED_URL_SUCCESS) {
            const body = new Map(action.response.requestBodyparams);

            if (isImage(file)) {
              // tslint:disable-next-line:no-unused-expression
              new Compressor(file, {
                quality: 0.7,
                success(compressedFile: Blob) {
                  s3uploadFile(body, compressedFile, (ev) => onUploadProgress(ev, fileKey)).then(
                    (response) => {
                      onUploadComplete(response, fileKey);
                    },
                    (error) => {
                      onUploadFailed(error, fileKey);
                    }
                  );
                },
              });
            } else {
              s3uploadFile(body, file, (ev) => onUploadProgress(ev, fileKey)).then(
                (response) => {
                  onUploadComplete(response, fileKey);
                },
                (error) => {
                  onUploadFailed(error, fileKey);
                }
              );
            }
          } else {
            onUploadFailed(action.error, fileKey);
          }
        });

        this.setState({
          files: newFiles,
        });
      };

      if (file.size < this.props.maxFileSize) {
        if (isImage(file)) {
          // tslint:disable-next-line:no-unused-expression
          new Compressor(file, {
            quality: 0.7,
            success: (compressedFile) => {
              reader.readAsDataURL(compressedFile);
            },
          });
        } else {
          reader.readAsDataURL(file);
        }
      } else {
        if (this.props.addError) {
          const maxFileSizeInMB = this.props.maxFileSize / (1024 * 1024);
          this.props.addError(`File size cannot exceed ${maxFileSizeInMB}MB`);
        }
      }
    });
    e.target.value = null;
  };

  getAcceptedFileTypesString = (): string =>
    this.props.acceptedFileTypes ? this.props.acceptedFileTypes.join(', ') : '';

  getFileType = (file: File) => {
    const fileNameSplit: string[] = file.name.split('.');
    const fileNameExtension: string = fileNameSplit[fileNameSplit.length - 1];

    return /(jpe?g|png)$/.test(fileNameExtension.toLowerCase()) ? 'image' : 'others';
  };

  getThumb = (file: IFileUpload) => {
    const fileTypeToThumb = {
      image: (
        <AttachmentImage
          className={classnames('photos-tile__saved-thumb')}
          src={file.data || file.s3SignedURL}
          alt="Attachment thumbnail"
        />
      ),
      others: <div className={classnames('photos-tile__saved-thumb', 'photos-tile__saved-thumb--others')} />,
    };

    return fileTypeToThumb[this.getFileType(file.details)];
  };

  getIssueSummaryThumb = (file: ICRMIssueAttachment) => {
    const fileTypeToThumb = {
      image: (
        <AttachmentImage
          className={classnames('photos-tile__saved-thumb')}
          src={file.fileURL}
          alt="Attachment thumbnail"
        />
      ),
      others: <div className={classnames('photos-tile__saved-thumb', 'photos-tile__saved-thumb--others')} />,
    };

    return fileTypeToThumb[getFileType(file)];
  };

  renderPhotos = () => (
    <div className={classnames('photos-tile__photos', 'tile-contents')}>
      {this.state.files.size > 0 &&
        Array.from(this.state.files.keys()).map((fileKey, index, map) => {
          const file = this.state.files.get(fileKey);
          const hasError = file?.error?.file?.errorState === IValidationState.ERROR;

          return (
            <div key={index} className={classnames('photos-tile__photo-container')}>
              <div className={classnames('delete-photo')}>
                <button
                  type="button"
                  className={classnames('delete-button')}
                  onClick={() => this.removeFile(fileKey)}
                  aria-label="Remove attachment"
                >
                  <img src="/assets/close-blue.png" alt="" />
                </button>
              </div>
              {this.getThumb(file)}
              {hasError && (
                <span role="alert" aria-live="polite" className={classnames('field__error', 'file-upload-error')}>
                  {file.error.file.errorMessage}
                </span>
              )}
            </div>
          );
        })}
    </div>
  );

  onCustomFieldChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const targetName = e.currentTarget.name;
    const targetValue = e.currentTarget.value;

    this.setState((prevState) => ({
      categoryfieldValues: {
        ...prevState.categoryfieldValues,
        [targetName]: {
          ...prevState.categoryfieldValues[targetName],
          value: targetValue,
        },
      },
    }));
  };

  handleAddNewFieldValue = (curr: ICRMSecondaryCustomField) => {
    const categoryFieldValues = this.state.categoryfieldValues;
    const newFieldValue = {
      id: curr._id,
      isRequired: curr.isRequired,
      label: curr.label,
      type: curr.type,
      value: '',
      error: getEmptyErrorState(),
    };
    categoryFieldValues[curr._id] = newFieldValue;
    this.setState((prevState) => ({
      ...prevState,
      categoryFieldValues,
    }));
  };

  removeFieldValue = (curr: ICRMSecondaryCustomField) => {
    const fieldValue = this.state.categoryfieldValues;
    if (fieldValue.hasOwnProperty(curr._id)) {
      delete fieldValue[curr._id];
    }
    this.setState((prevState) => ({
      ...prevState,
      categoryfieldValues: fieldValue,
    }));
  };

  renderSelectedCategory = (selectedCategory: ICRMSecondaryCustomField, value: any) => {
    const { categoryfieldValues } = this.state;
    let displaySecondaryCustomField = false;

    if ('condition' in selectedCategory) {
      const { operator, value: conditionValue } = selectedCategory.condition;
      switch (operator) {
        case 'equals':
          displaySecondaryCustomField = value.toLowerCase() === conditionValue.toLowerCase();
          break;
        default:
          displaySecondaryCustomField =
            (conditionValue.toLowerCase() !== '' && value?.toLowerCase().includes(conditionValue.toLowerCase())) ||
            value.toLowerCase() === operator.toLowerCase();
          break;
      }
    }
    const isValid = selectedCategory.isActive && !selectedCategory.isDeleted;
    if (isValid) {
      const hasField = categoryfieldValues.hasOwnProperty(selectedCategory._id);
      if (displaySecondaryCustomField && !hasField) {
        this.handleAddNewFieldValue(selectedCategory);
      } else if (!displaySecondaryCustomField && hasField) {
        this.removeFieldValue(selectedCategory);
      }
    }

    return (
      displaySecondaryCustomField &&
      selectedCategory.isActive &&
      !selectedCategory.isDeleted && (
        <Input
          key={selectedCategory.label}
          field={{
            isRequired: selectedCategory.isRequired,
            label: selectedCategory.label,
            type: selectedCategory.type,
            options: selectedCategory.options,
            value:
              categoryfieldValues &&
              categoryfieldValues[selectedCategory._id] &&
              categoryfieldValues[selectedCategory._id].value,
          }}
          name={`${selectedCategory._id}`}
          error={
            categoryfieldValues &&
            categoryfieldValues[selectedCategory._id] &&
            categoryfieldValues[selectedCategory._id].error
          }
          onChange={this.onCustomFieldChange}
          widthMD={3}
        />
      )
    );
  };

  renderIssueDescriptionForm = () => {
    const { description, error } = this.state;
    const { categoryfieldValues, category: selectedCategory } = this.state;

    const options = this.props.categories?.map((category) => ({
      ...category,
      label: category.name,
      color: 'white',
    }));

    let filteredSuggestedCategories = [];
    this.props.categories?.forEach((cat) => {
      if (this.state.suggestedCategories.includes(cat.id)) {
        filteredSuggestedCategories.push(cat);
      }
    });

    filteredSuggestedCategories = filteredSuggestedCategories.map((category) => ({
      ...category,
      label: category.name,
      color: 'grey',
    }));

    return (
      <>
        <MultiSectionDropdown
          options={options}
          suggestedOptions={filteredSuggestedCategories}
          handleCategorySelect={this.handleCategorySelection}
          getSuggestedCategories={(crmToken, desc) => this.props.getSuggestedCategories(crmToken, desc)}
          crmToken={this.props.match.params.crmToken}
          error={!this.state.categoryFilled ? 'The Category is required.' : ''}
        />
        <div className={classnames('issue-detail-tile__body__custom-fields')}>
          {selectedCategory &&
            (selectedCategory.fields || [])
              .filter((field) => field.isActive && !field.isDeleted)
              .map((field: any, index) => (
                <>
                  <Input
                    key={field.label}
                    field={{
                      isRequired: field.isRequired,
                      label: field.label,
                      type: field.type,
                      options: field.options,
                      value:
                        categoryfieldValues && categoryfieldValues[field._id] && categoryfieldValues[field._id].value,
                    }}
                    name={`${field._id}`}
                    error={
                      categoryfieldValues && categoryfieldValues[field._id] && categoryfieldValues[field._id].error
                    }
                    onChange={this.onCustomFieldChange}
                    widthMD={3}
                  />
                  {field?.fields?.map((nestedFields: ICRMSecondaryCustomField) =>
                    this.renderSelectedCategory(nestedFields, categoryfieldValues[field._id].value)
                  )}
                </>
              ))}
        </div>
        <Form className={classnames('crm-submission__form')}></Form>
        <Form className={classnames('crm-submission__form')}>
          <Row>
            <Input
              width={12}
              field={{
                label: 'Description',
                type: CustomFieldType.TEXTAREA,
                value: description,
                isRequired: true,
              }}
              error={error.description}
              name="description"
              onChange={this.handleChange}
            />
          </Row>
          <div className={classnames('field-section')}>
            <div>
              {this.state.files.size > 0 && this.renderPhotos()}
              <div
                onDragEnter={this.onDragEnter}
                onDragExit={this.onDragExit}
                onDragLeave={this.onDragLeave}
                onDragOver={this.onDragOver}
                onDrop={this.onDrop}
                className={classnames('photos__drag-drop-box')}
              >
                {isDesktop() ? (
                  <div className={classnames('desktop-view')}>
                    <span className={classnames('file-upload__cloud-img')} />
                    <span>Drag & Drop or </span>
                    <label htmlFor="file-upload" className={classnames('label-desktop')}>
                      Browse file
                    </label>
                    <input
                      id="file-upload"
                      type="file"
                      onChange={(e) => this.handleAttachmentChange(e)}
                      accept={this.getAcceptedFileTypesString()}
                      multiple={true}
                    />
                  </div>
                ) : (
                  <div className={classnames('mobile-view')}>
                    <label htmlFor="file-upload" className={classnames('label-mobile')}>
                      <span className={classnames('file-upload__add-img')} />
                      <span>Attachments</span>
                    </label>
                    <input
                      id="file-upload"
                      type="file"
                      onChange={(e) => this.handleAttachmentChange(e)}
                      accept={this.getAcceptedFileTypesString()}
                      multiple={true}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className={classnames('field-section attachments-modal__disclaimer')}>
            <span className={classnames('attachments-modal__disclaimer-icon')} />
            <div className={classnames('attachments-modal__disclaimer-msg')}>
              <span>Maximum file size (each file) is 25MB</span>
              <span>{`Accepted file types are ${this.getAcceptedFileTypesString()}.`}</span>
            </div>
          </div>
        </Form>
      </>
    );
  };

  setLocationError = (locationError: IFieldValidation) => {
    this.setState({
      error: { ...this.state.error, location: locationError },
    });
  };

  renderLocationForm = () => (
    <Form className={classnames('crm-submission__form')}>
      <Row>
        <div className={classnames('crm-location-section')}>
          <CRMLocationField
            error={this.state.error.location}
            markerCenter={this.state.markerCenter}
            location={locationUtil.getFullAddressFromLocation(this.state.location, this.props.gisVersion)}
            lat={this.state.location && this.state.location.lat}
            long={this.state.location && this.state.location.long}
            onLocationChange={this.handleLocationChange}
            onMarkerChange={this.handleMarkerChange}
            crmToken={this.props.match.params.crmToken}
            setLocationError={this.setLocationError}
            isManualAddress={this.state.location && this.state.location.isManualAddress}
          />
          <span className={classnames('field__error')}>
            {this.state.error.location.errorState === IValidationState.ERROR ||
            this.state.error.location.errorState === IValidationState.WARNING
              ? this.state.error.location.errorMessage
              : ''}
          </span>
        </div>
      </Row>
    </Form>
  );

  handleEmailButtonClick = (e: React.SyntheticEvent<any>) => {
    const { isEmailNotificationPreferenceSelected } = this.state;
    this.setState((prevState) => ({
      ...prevState,
      isEmailNotificationPreferenceSelected: !isEmailNotificationPreferenceSelected,
    }));
  };

  handleSMSButtonClick = (e: React.SyntheticEvent<any>) => {
    const { isSMSNotificationPreferenceSelected } = this.state;

    this.setState((prevState) => ({
      ...prevState,
      isSMSNotificationPreferenceSelected: !isSMSNotificationPreferenceSelected,
    }));
  };

  renderContactInfoForm = () => {
    const {
      isAnonymous,
      hasSubscribed,
      firstName,
      lastName,
      email,
      phoneNumber,
      workPhone,
      error,
      isEmailNotificationPreferenceSelected,
      isSMSNotificationPreferenceSelected,
      notificationNote,
    } = this.state;

    return (
      <>
        <Row>
          <div className="field-section">
            <div className="checkbox-container">
              <Checkbox
                onChange={this.handleSubscriptionChange}
                isChecked={hasSubscribed}
                name="hasSubscribed"
                disabled={isAnonymous}
              >
                I want to receive updates on my submission
              </Checkbox>
            </div>
          </div>
        </Row>
        <Row>
          <div className="field-section">
            <h5>Personal Information</h5>
          </div>
          <div>
            <Input
              field={{
                isRequired: false,
                label: 'First Name',
                type: CustomFieldType.TEXT,
                value: firstName,
              }}
              name="firstName"
              error={error.firstName}
              onChange={this.handleChange}
              widthMD={6}
              disabled={isAnonymous}
            />
          </div>
          <div>
            <Input
              field={{
                isRequired: false,
                label: 'Last Name',
                type: CustomFieldType.TEXT,
                value: lastName,
              }}
              name="lastName"
              error={error.lastName}
              onChange={this.handleChange}
              widthMD={6}
              disabled={isAnonymous}
            />
          </div>
        </Row>
        <Row />
        <Row>
          <div className="field-section">
            <h5>Contact Information</h5>
          </div>
          <div>
            <Input
              field={{
                isRequired: false,
                label: 'Email',
                type: CustomFieldType.TEXT,
                value: email,
              }}
              name="email"
              error={error.email}
              onChange={this.handleChange}
              onBlur={this.handleBlur}
              widthMD={6}
              disabled={isAnonymous}
            />
          </div>
          <div>
            {this.props.isSMSEnabled && (
              <Input
                field={{
                  isRequired: false,
                  label: 'Cell Phone',
                  type: CustomFieldType.PHONE,
                  value: phoneNumber,
                }}
                name="phoneNumber"
                error={error.phoneNumber}
                onChange={this.handleChange}
                onBlur={this.handleBlur}
                widthMD={6}
                disabled={isAnonymous}
                placeholder="(xxx) xxx-xxxx"
              />
            )}
          </div>
          <div>
            {this.props.isSMSEnabled && (
              <Input
                field={{
                  isRequired: false,
                  label: 'Work Phone',
                  type: CustomFieldType.PHONE,
                  value: workPhone,
                }}
                name="workPhone"
                error={error.workPhone}
                onChange={this.handleChange}
                widthMD={6}
                disabled={isAnonymous}
                placeholder="(xxx) xxx-xxxx"
              />
            )}
          </div>
        </Row>
        <Row>
          <div className="field-section">
            <h5>Automated Update Preference</h5>
            <p>How would you like to be updated?</p>
            {this.renderMultiSelectButton(
              isEmailNotificationPreferenceSelected,
              isSMSNotificationPreferenceSelected,
              isAnonymous
            )}
            <h5 className="notification-note-others">Other</h5>
          </div>
          <Input
            field={{
              isRequired: false,
              label: '',
              type: CustomFieldType.TEXT,
              value: notificationNote,
            }}
            name="notificationNote"
            error={error.notificationNote}
            onChange={this.handleChange}
            widthMD={6}
            disabled={isAnonymous}
          />
          {this.props.contactPreferenceNote && (
            <p className="crm__anonymous-toggle-note">
              <b>Note:</b> {this.props.contactPreferenceNote}
            </p>
          )}
        </Row>
      </>
    );
  };

  renderContactForm = () => {
    const { isAnonymous } = this.state;

    if (this.state.activeStep < this.getLastStep() + 1 || this.props.issue) {
      return null;
    }
    const postAnonymousOption = [
      { value: false, label: 'No' },
      { value: true, label: 'Yes' },
    ];

    return (
      <>
        <Form className={classnames('crm-submission__form', 'crm__contact-form')}>
          <Row>
            <div className="crm__anonymous-toggle col">
              <h5>Stay Updated on the Progress of your Submission</h5>
            </div>
            {this.props.postAnonymous && (
              <div>
                <label id="isAnonymous-label" className="anonymous-field-label" htmlFor="isAnonymous-radio-group">
                  Do you want your Submission to be Anonymous?
                </label>
                <div
                  id="isAnonymous-radio-group"
                  className="anonymous-radio-btn"
                  role="radiogroup"
                  aria-labelledby="isAnonymous-label"
                >
                  <Input
                    width={5}
                    field={{
                      type: OtherFieldType.RADIOBUTTON,
                      value: isAnonymous,
                      isRequired: true,
                    }}
                    name="isAnonymous"
                    valueLabelOptions={postAnonymousOption}
                    onChange={this.handleAnonymousChange}
                  />
                </div>
              </div>
            )}
          </Row>
          {!isAnonymous && this.renderContactInfoForm()}
        </Form>
        {this.renderActions()}
      </>
    );
  };

  renderMultiSelectButton = (
    isEmailNotificationPreferenceSelected: boolean,
    isSMSNotificationPreferenceSelected: boolean,
    isAnonymous: boolean
  ) => (
    <>
      <Button
        className={
          isEmailNotificationPreferenceSelected ? 'preference-selected-button' : 'preference-notselected-button'
        }
        onClick={this.handleEmailButtonClick}
        content="Email"
        disabled={isAnonymous}
      />
      {this.props.isSMSEnabled && (
        <Button
          className={
            isSMSNotificationPreferenceSelected ? 'preference-selected-button' : 'preference-notselected-button'
          }
          onClick={this.handleSMSButtonClick}
          content="Text Message"
          disabled={isAnonymous}
        />
      )}
    </>
  );

  renderActions = () => (
    <Row className={classnames('actions')}>
      <Button bsStyle={ButtonStyle.DEFAULT} content="Back" onClick={() => this.moveToStep(this.getLastStep())} />
      <Button
        bsStyle={ButtonStyle.PRIMARY}
        content="Submit"
        onClick={this.handleSubmit}
        disabled={this.isAddDisabled()}
        showLoader={this.isAddDisabled()}
      />
    </Row>
  );

  renderStepTitle = (step: number, title: string) => (
    <div
      className={classnames('crm-submission__steps__title', {
        disabled: this.state.activeStep !== step && this.state.activeStep < step,
      })}
    >
      <div className="crm-submission__steps__title__number">{step + 1}</div>
      <h2 className="crm-submission__steps__title__heading">{title}</h2>
    </div>
  );

  moveToStep = (step: number) => {
    this.saveAnonymousDetailsIFrame();
    this.setState({ activeStep: step }, () =>
      setTimeout(() => {
        if (step > this.getLastStep()) {
          scrollTo('.crm-submission__navigation-bar')();

          return;
        }
        scrollTo(`#crm-submission__steps__step-${step}`)();
        // tslint:disable-next-line
      }, 350)
    );
  };

  getLastStep = () => {
    const { category } = this.state;

    return category && category.isLocationIncluded ? 1 : 0;
  };

  validateCategoryCustomFieldForm = () => {
    const { categoryfieldValues } = this.state;

    const { fieldValues, isValid } = customFieldUtil.validateCategoryFieldValues(categoryfieldValues);

    this.setState({
      categoryfieldValues: fieldValues,
    });

    return isValid;
  };

  renderAccordionSteps = () => {
    const { activeStep, category } = this.state;

    if (activeStep > this.getLastStep()) {
      return null;
    }

    const buttonText = 'Next';

    const steps = [
      {
        title: 'Request Details',
        content: () => this.renderIssueDescriptionForm(),
        validate: this.validateDescriptionForm,
        onSubmit: this.onDescriptionSubmit,
      },
      ...(category && category.isLocationIncluded
        ? [
            {
              title: 'Location',
              content: () => this.renderLocationForm(),
              validate: this.validateLocationForm,
            },
          ]
        : []),
    ];

    return (
      <div className="crm-submission__steps">
        {steps.map((step, index) => (
          <div id={`crm-submission__steps__step-${index}`} key={`step-${index}`}>
            <Accordion
              key={`accordion-${index}`}
              title={this.renderStepTitle(index, step.title)}
              isDisabled={index > activeStep}
              isExpanded={activeStep === index}
              hideBorder={index === steps.length - 1}
              onClick={() => this.moveToStep(index)}
            >
              {activeStep >= index && step.content()}
              {activeStep >= index && (
                <Row className="crm-submission__steps__actions">
                  <Button
                    bsStyle={ButtonStyle.PRIMARY}
                    disabled={this.isAddDisabled()}
                    showLoader={this.isAddDisabled()}
                    content={buttonText}
                    onClick={async () => {
                      if (step.validate && !step.validate()) {
                        return;
                      }
                      if (step.onSubmit) {
                        await step.onSubmit();
                      }

                      this.moveToStep(this.state.activeStep + 1);
                    }}
                  />
                </Row>
              )}
            </Accordion>
          </div>
        ))}
      </div>
    );
  };

  renderReviewSummary = () => {
    const {
      issue: { isAnonymous, name, description, email, attachments, location, category, phoneNumber },
    } = this.props;
    const { category: selectedCategory } = this.state;

    const locationString = location
      ? locationUtil.getFullAddressFromLocation(
          {
            ...location,
            lat: location.latitude,
            long: location.longitude,
          },
          this.props.gisVersion
        )
      : '-';

    return (
      <div className={classnames('crm__review-summary')}>
        <section className={classnames('crm__success-message-section')}>
          <h5>Request submitted successfully</h5>
          <p>The request has been submitted successfully. Thank you.</p>
        </section>
        <section className={classnames('crm__review-info-section')}>
          <h5>Review information</h5>
          <div className={classnames('crm__review-summary__field')}>
            <label htmlFor="issue-description">Description</label>
            <p id="issue-description" className={classnames('issue-description')}>
              {description}
            </p>
          </div>
          {attachments.length > 0 && (
            <div className={classnames('crm__review-summary__field')}>
              <label htmlFor="attachments">Attachments</label>
              <div id="attachments" className={classnames('photos-tile__photos')}>
                {attachments.map((attachment) => (
                  <div key={attachment.id} className={classnames('photos-tile__photo-container')}>
                    {this.getIssueSummaryThumb(attachment)}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className={classnames('crm__review-summary__field')}>
            <label htmlFor="category">Category</label>
            <p id="category" className={classnames('category')}>
              {(category ? category.name : '-').trim()}
            </p>
          </div>

          {selectedCategory && selectedCategory.isLocationIncluded && (
            <div className={classnames('crm__review-summary__field')}>
              <label htmlFor="location">Location</label>
              <p id="location" className={classnames('location-details')}>
                {locationString.trim() || '-'}
              </p>
            </div>
          )}
          {!isAnonymous && (
            <div className={classnames('crm__review-summary__field')}>
              <label htmlFor="contact-information">Contact information</label>
              <div id="contact-information" className={classnames('contact-details')}>
                <div className={classnames('fields')}>
                  <label htmlFor="contact-name">Name:</label>
                  <span id="contact-name">{name || '-'}</span>
                </div>
              </div>
              <div className={classnames('contact-details')}>
                <div className={classnames('fields')}>
                  <label htmlFor="contact-email">Email:</label>
                  <span id="contact-email">{email || '-'}</span>
                </div>
                <div className={classnames('fields')}>
                  <label htmlFor="contact-phone">Cell Phone:</label>
                  <span id="contact-phone">{phoneNumber || '-'}</span>
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    );
  };

  render() {
    return (
      <>
        {this.renderNavigationBar()}
        <main className="crm-submission__main">
          {this.props.issue ? this.renderReviewSummary() : this.renderAccordionSteps()}
          {this.renderContactForm()}
        </main>
      </>
    );
  }
}

const mapStateToProps = (state: IReduxStore) => ({
  agencyDetails: state.crm.agencyDetails,
  issue: state.crm.issue,
  categories:
    state.crm &&
    state.crm.config &&
    state.crm.config.categories?.filter(
      (category) => category.categoryType === CategoryType.EXTERNAL && category.isActive
    ),
  isSMSEnabled: state.crm && state.crm.config && state.crm.config.isSMSEnabled,
  postAnonymous: state.crm && state.crm.config && state.crm.config.postAnonymous,
  anonymousSubmission: state.crm && state.crm.anonymousSubmission,
  contactPreferenceNote: state.crm.config && state.crm.config.contactPreferenceNote,
  gisVersion: state.productLayout.productLayout?.agency?.agencyInfo?.gisVersion,
});

const mapDispatchToProps = (dispatch: any) => ({
  validateCRMToken: (crmToken: string) => dispatch(validateCRMToken(crmToken)),
  saveCRMAnonymousIssueDetails: (crmToken: string, anonymousId: string, issueDetails: ICRMIssueAnonymousDetails) =>
    dispatch(saveCRMAnonymousIssueDetails(crmToken, anonymousId, issueDetails)),
  getCRMAnonymousIssueDetails: (crmToken: string, anonymousId: string) =>
    dispatch(getCRMAnonymousIssueDetails(crmToken, anonymousId)),
  submitIssue: (crmToken: string, issueRequest: ICRMIssueSubmissionRequest) =>
    dispatch(submitIssue(crmToken, issueRequest)),
  getCRMSignedURL: (crmToken: string, operation: S3Operation, fileName: string) =>
    dispatch(getCRMSignedURL(crmToken, operation, fileName)),
  getSuggestedCategories: (crmToken: string, description: string) =>
    dispatch(getCRMSuggestedCategories(crmToken, description)),
  addError: (errorMessage: string) => dispatch(addError(errorMessage)),
  getCRMMapConfig: (crmToken: string) => dispatch(getCRMMapConfig(crmToken)),
  getSsoToken: (crmToken: string, authCode: string) => dispatch(getSSOToken(crmToken, authCode)),
  getSsoConfig: (crmToken: string) => dispatch(getSSOConfig(crmToken)),
  getSsoUserInfo: (crmToken: string, idpUserInfoRequest: ISsoUserInfoRequest) =>
    dispatch(getSSOUserInfo(crmToken, idpUserInfoRequest)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CRMIssueForm);
